import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, List, X, UserCircle, SignOut,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import Avatar from "@/components/Avatar";

// Brand-dark navbar background. Chosen to maximise contrast for the
// white-on-transparent MarketVyne logo while staying inside the
// charcoal/green family already used by the brand. (See branding spec.)
const NAV_BG = "#07131F";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/browse?q=${encodeURIComponent(q.trim())}`);
  };

  const dashHref = !user
    ? "/login"
    : user.role === "admin"
    ? "/dashboard/admin"
    : user.role === "seller"
    ? "/dashboard/seller"
    : "/dashboard/buyer";

  const navLinkCls = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      isActive ? "text-mv-accent" : "text-gray-200 hover:text-mv-accent"
    }`;

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/10"
      style={{ backgroundColor: NAV_BG }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center group shrink-0" data-testid="logo-link" aria-label="MarketVyne home">
            {/* Mobile: icon mark (compact, no crowding on 360px viewports).
                Desktop: full lockup at 40px tall — balanced inside the h-16 navbar. */}
            <Logo variant="mark" height={32} className="sm:hidden" />
            <Logo variant="full" height={40} className="hidden sm:block" />
          </Link>

          {/* desktop search — show from lg+ so the md/tablet width keeps room for nav links */}
          <form
            onSubmit={onSearch}
            className="hidden lg:flex flex-1 max-w-md mx-4 relative"
            data-testid="header-search-form"
          >
            <MagnifyingGlass size={18} weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="header-search-input"
              placeholder="Search services, e.g. SEO audit"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-full border border-white/15 bg-white/5 text-white placeholder:text-gray-400 focus:bg-white/10 focus:border-mv-accent focus:ring-2 focus:ring-mv-accent/30 text-sm outline-none transition-colors"
            />
          </form>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/browse" className={navLinkCls} data-testid="nav-browse">
              Browse
            </NavLink>
            <NavLink to="/how-it-works" className={navLinkCls} data-testid="nav-how-it-works">
              How it works
            </NavLink>
            <NavLink to="/become-a-seller" className={navLinkCls} data-testid="nav-become-seller">
              Become a seller
            </NavLink>
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {!user ? (
              <>
                <Link to="/login" data-testid="login-btn-header">
                  <Button variant="ghost" className="text-white hover:text-mv-accent hover:bg-white/5 font-medium">Log in</Button>
                </Link>
                <Link to="/register" data-testid="register-btn-header">
                  <Button className="bg-mv-accent hover:bg-mv-accent/90 text-mv-secondary font-bold rounded-lg">
                    Get started
                  </Button>
                </Link>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn"
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                    <Avatar src={user.avatar_url} name={user.name} size={32} testId="header-user-avatar" />
                    <span className="text-sm font-medium text-white">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-display font-bold">{user.name}</div>
                    <div className="text-xs text-gray-500 capitalize">{user.role}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav(dashHref)} data-testid="menu-dashboard">
                    Dashboard
                  </DropdownMenuItem>
                  {user.role === "buyer" && (
                    <DropdownMenuItem onClick={() => nav("/dashboard/buyer/saved")}>Saved</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => nav("/messages")}>Messages</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">
                    <SignOut size={16} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <button
            className="md:hidden p-2 text-white hover:text-mv-accent transition-colors"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 space-y-3" data-testid="mobile-menu">
            <form onSubmit={onSearch} className="relative">
              <MagnifyingGlass size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="mobile-search-input"
                placeholder="Search services"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-full border border-white/15 bg-white/5 text-white placeholder:text-gray-400 text-sm outline-none focus:border-mv-accent" />
            </form>
            <Link to="/browse" className="block py-2 text-gray-200 hover:text-mv-accent">Browse</Link>
            <Link to="/how-it-works" className="block py-2 text-gray-200 hover:text-mv-accent">How it works</Link>
            <Link to="/become-a-seller" className="block py-2 text-gray-200 hover:text-mv-accent">Become a seller</Link>
            {user ? (
              <>
                <Link to={dashHref} className="block py-2 text-mv-accent font-bold">Dashboard</Link>
                <button onClick={async () => { await logout(); nav("/"); }} className="block py-2 text-gray-300">Log out</button>
              </>
            ) : (
              <div className="flex gap-2 pt-2">
                <Link to="/login" className="flex-1"><Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5">Log in</Button></Link>
                <Link to="/register" className="flex-1"><Button className="w-full bg-mv-accent text-mv-secondary hover:bg-mv-accent/90 font-bold">Get started</Button></Link>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
